import React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Lock, LockOpen, MoreVertical, Pencil, Play, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { costContractStatusVariant } from '@/lib/status-variants'
import { CONTRACT_KIND_KEY, CONTRACT_STATUS_KEY, type ContractAffordances } from '../lib/contracts'
import type { CostContract, CostContractStatus } from '../types'

interface Props {
  contract: CostContract
  af: ContractAffordances
  /** i18n key: why activation would be refused, or null. */
  blocker: string | null
  onBack: () => void
  onEdit: () => void
  onStatus: (to: CostContractStatus) => void
  onDelete: () => void
}

const ContractHeader: React.FC<Props> = ({ contract, af, blocker, onBack, onEdit, onStatus, onDelete }) => {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-start gap-3">
      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onBack} aria-label={t('CostControl_contracts_back')}>
        <ArrowLeft className="h-4 w-4" />
      </Button>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          {contract.number && <span className="font-mono text-sm text-muted-foreground">{contract.number}</span>}
          <h1 className="truncate text-xl font-semibold">{contract.name}</h1>
          <Badge variant={costContractStatusVariant(contract.status)}>{t(CONTRACT_STATUS_KEY[contract.status])}</Badge>
          {contract.kind === 'MAIN' && <Badge variant="secondary">{t(CONTRACT_KIND_KEY.MAIN)}</Badge>}
        </div>
        <p className="text-sm text-muted-foreground">{contract.counterpartyName}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {af.activate && (
          <Button size="sm" disabled={!!blocker} onClick={() => onStatus('ACTIVE')}>
            <Play className="h-4 w-4" />
            {t('CostControl_contract_activate')}
          </Button>
        )}
        {af.close && (
          <Button variant="outline" size="sm" onClick={() => onStatus('CLOSED')}>
            <Lock className="h-4 w-4" />
            {t('CostControl_contract_close')}
          </Button>
        )}
        {af.reopen && (
          <Button variant="outline" size="sm" onClick={() => onStatus('ACTIVE')}>
            <LockOpen className="h-4 w-4" />
            {t('CostControl_contract_reopen')}
          </Button>
        )}
        {af.editHeader && (
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
            {t('Common_edit')}
          </Button>
        )}
        {af.remove && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t('CostControl_contract_more')}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={onDelete}>
                <Trash2 className="h-4 w-4" />
                {t('Common_delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {af.activate && blocker && <p className="w-full pl-11 text-xs text-muted-foreground">{t(blocker)}</p>}
    </div>
  )
}
export default ContractHeader
